import React, { useCallback, type PropsWithChildren } from 'react';
import {
	useJiraInvitePeopleModalBound,
	JiraInvitePeopleModalContextProvider,
} from '@jira/project-settings__invite-people-modal-context/src/JiraInvitePeopleModalContextProvider.tsx';
import {
	InvitePeopleModalContextProvider as BaseInvitePeopleModalContextProvider,
	useInvitePeopleModal as useInvitePeopleModalPTC,
} from '@atlassian/invite-people/InvitePeopleModalContext';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';

/*
 * When we cleaning up experiment 'jira-invite-people-to-project-modal', this file should be removed.
 * Depending on the success / failure of the expirement we can replace the import of the modal context and hook
 * with the one from the invite-people package or keep using the one from jira-invite-people-modal-context.
 */

const isJiraInvitePeopleModalEnabled = () => {
	const [config] = UNSAFE_noExposureExp('jira-invite-people-to-project-modal');
	return config.get('isEnabled', false);
};

type UseInvitePeopleModalPTC = typeof useInvitePeopleModalPTC;

type UseInvitePeopleModalInner = (
	...params: Parameters<UseInvitePeopleModalPTC>
) => Pick<ReturnType<UseInvitePeopleModalPTC>, 'openModal'>;

// this ensures we only load one instance of the hook
const useConditionalInvitePeopleModal = functionWithCondition<UseInvitePeopleModalInner>(
	() => isJiraInvitePeopleModalEnabled(),
	// eslint-disable-next-line jira/react/no-usage-of-hooks-outside-components
	({ resourceAri, onSubmit, ...props }) => useJiraInvitePeopleModalBound(props),
	useInvitePeopleModalPTC,
);

export const useInvitePeopleModal: UseInvitePeopleModalInner = (props) => {
	const [, fireExposure] = UNSAFE_noExposureExp('jira-invite-people-to-project-modal');

	const { openModal } = useConditionalInvitePeopleModal(props);

	const openModalWithExposure = useCallback(() => {
		fireExposure();
		openModal();
	}, [openModal, fireExposure]);

	return { openModal: openModalWithExposure };
};

export const InvitePeopleModalContextProvider = ({ children }: PropsWithChildren) => {
	if (isJiraInvitePeopleModalEnabled()) {
		return (
			<JiraInvitePeopleModalContextProvider
				renderedInProjectContext={false}
				shouldDisplaySuccessModal={false}
			>
				{children}
			</JiraInvitePeopleModalContextProvider>
		);
	}

	return <BaseInvitePeopleModalContextProvider>{children}</BaseInvitePeopleModalContextProvider>;
};
